import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { useGroupScope } from '@/context/GroupScopeContext';
import styles from './PlatformLayout.module.css';

/**
 * F3 — the "Scope: <group>" control in the console top bar.
 *
 * Only rendered for operators who actually hold group contexts; someone without
 * one gets no control, because a menu whose only entry is "you have no groups"
 * would be noise on every page of the console. Selecting a group records it as
 * the working scope and lands on the group's overview — the same deep link the
 * groups list uses, so one action cannot take the operator somewhere the rest
 * of the console does not know how to reach.
 */
export function GroupScopeSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupContexts, activeGroupId, setActiveGroupId } = useGroupScope();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open]);

  if (groupContexts.length === 0) return null;

  const activeGroup = groupContexts.find((ctx) => ctx.groupId === activeGroupId) ?? null;

  const select = (groupId: string) => {
    setActiveGroupId(groupId);
    setOpen(false);
    navigate(`/admin/groups/${groupId}`);
  };

  return (
    <div ref={panelRef} className={styles.scopeSwitcher}>
      <button
        type="button"
        className={styles.scopeBtn}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('platform.groups.scope.label')}
      >
        <span className={styles.scopeLabel}>{t('platform.groups.scope.label')}</span>
        <strong>{activeGroup?.groupName ?? t('platform.groups.scope.noScope')}</strong>
        <Icon name="chevronDown" size={14} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.scopeMenu} role="menu" aria-label={t('platform.groups.scope.menuLabel')}>
          <div className={styles.scopeMenuHeader}>{t('platform.groups.scope.menuLabel')}</div>
          <div className={styles.scopeMenuList}>
            {groupContexts.map((ctx) => (
              <button
                key={ctx.id}
                type="button"
                role="menuitemradio"
                aria-checked={ctx.groupId === activeGroupId}
                className={`${styles.scopeMenuItem} ${ctx.groupId === activeGroupId ? styles.scopeMenuItemActive : ''}`}
                onClick={() => ctx.groupId && select(ctx.groupId)}
              >
                <span>{ctx.groupName ?? ''}</span>
                <span className={styles.scopeMenuItemType}>{ctx.groupType ?? ''}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
